import { stringify } from 'qs';
import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { FossabotStatusResponse } from '../../../../shared/fossabot/response';

const POLL_INTERVAL_MS = 3000;

function fetchFossabotConnectionStatus(projectLocator: string) {
  const queryString = stringify({ projectLocator }, { addQueryPrefix: true });
  return get<FossabotStatusResponse>(`/api/fossabot/status${queryString}`);
}

export const getFossabotConnectionStatusQueryKey = (projectLocator: string): QueryKey => [
  'fossabot',
  'status',
  projectLocator,
];

/**
 * Queries the repository-scoped fossabot connection status. While an installed
 * GitHub App is preparing a disconnected repository, the query polls until
 * fossabot reports a settled readiness state or connects the repository.
 */
export const useFossabotConnectionStatus = (
  projectLocator: string,
  isGitHubAppInstalled: boolean,
) => {
  return useQuery<FossabotStatusResponse>({
    queryKey: getFossabotConnectionStatusQueryKey(projectLocator),
    queryFn: () => fetchFossabotConnectionStatus(projectLocator),
    enabled: !!projectLocator,
    refetchInterval: (data) => {
      if (!isGitHubAppInstalled || !data?.statusAvailable || data.connected) {
        return false;
      }
      return data.readinessStatus === null || data.readinessStatus === 'SYNCING'
        ? POLL_INTERVAL_MS
        : false;
    },
  });
};
